/**
 * @fileoverview Metadata constants for SEO and social sharing
 * 
 * This module centralizes all metadata used throughout the application for:
 * - Open Graph (Facebook, LinkedIn)
 * - Twitter Cards
 * - WhatsApp sharing
 * - SEO structured data
 * - General meta tags
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

// Base URLs and domains
export const SITE_CONFIG = {
  BASE_URL: 'https://salaire-infirmiere-hopital.fr',
  DOMAIN: 'salaire-infirmiere-hopital.fr',
  CANONICAL_DOMAIN: 'https://salaire-infirmiere-hopital.fr',
  SITE_NAME: 'Simulateur Revenus IDE - App\'Ines'
} as const;

// Banner image configuration
export const BANNER_CONFIG = {
  IMAGE_URL: `${SITE_CONFIG.BASE_URL}/banner-simulateur-revenu-appines.png`,
  IMAGE_WIDTH: 1200,
  IMAGE_HEIGHT: 630,
  IMAGE_ALT: 'Simulateur de salaire pour infirmiers IDE - Calcul revenus fonction publique hospitalière'
} as const;

// Logo configuration
export const LOGO_CONFIG = {
  IMAGE_URL: `${SITE_CONFIG.BASE_URL}/logo-appines.png`,
  IMAGE_ALT: 'Logo App\'Ines - Plateforme emploi santé'
} as const;

// SEO Meta Tags
export const SEO_META = {
  TITLE: '💰 Simulateur de revenus - Infirmier Diplômé d\'État (IDE)',
  DESCRIPTION: 'Calculez votre salaire d\'infirmier diplômé d\'état dans la fonction publique hospitalière avec notre simulateur officiel. Grille indiciaire 2025, primes et indemnités incluses.',
  KEYWORDS: 'salaire infirmier IDE, fonction publique hospitalière, grille indiciaire, prime ségur, simulateur salaire, infirmier diplômé état, calcul salaire IDE, primes infirmier, échelon infirmier, grade infirmier',
  AUTHOR: 'Simulateur Revenus IDE',
  ROBOTS: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
} as const;

// Open Graph (Facebook, LinkedIn) metadata
export const OPEN_GRAPH_META = {
  TYPE: 'website',
  TITLE: '💰 Simulateur Salaire IDE - Calcul Revenus Infirmier 2025',
  DESCRIPTION: '🩺 Calculez votre salaire d\'infirmier IDE dans la fonction publique hospitalière ! Grille indiciaire 2025, primes Ségur, PSS, ISS incluses. Simulation gratuite et précise !',
  LOCALE: 'fr_FR'
} as const;

// Twitter Card metadata
export const TWITTER_META = {
  CARD: 'summary_large_image',
  TITLE: '💰 Simulateur Salaire IDE - Calcul Revenus Infirmier 2025',
  DESCRIPTION: '🩺 Calculez votre salaire d\'infirmier IDE ! Grille indiciaire 2025, primes Ségur, PSS, ISS. Simulation gratuite pour la fonction publique hospitalière !',
  SITE: '@AppInes_fr',
  CREATOR: '@AppInes_fr'
} as const;

// WhatsApp sharing metadata
export const WHATSAPP_META = {
  TITLE: '💰 Simulateur Salaire IDE 2025',
  DESCRIPTION: '🩺 Calcule ton salaire d\'infirmier IDE ! Grille 2025 + primes Ségur. Gratuit et précis !'
} as const;

// Theme and application metadata
export const APP_META = {
  THEME_COLOR: '#3B82F6',
  APPLICATION_NAME: 'Simulateur Salaire IDE',
  MSAPPLICATION_TILE_COLOR: '#3B82F6',
  MSAPPLICATION_CONFIG: '/browserconfig.xml'
} as const;

// Structured data for SEO
export const STRUCTURED_DATA_CONFIG = {
  SOFTWARE_APPLICATION: {
    NAME: 'Simulateur de Salaire Infirmier IDE',
    DESCRIPTION: 'Outil interactif pour estimer le salaire net des infirmiers en fonction publique hospitalière, incluant grades, échelons et primes. Calcul basé sur la grille indiciaire officielle 2025.',
    APPLICATION_CATEGORY: 'FinanceApplication',
    OPERATING_SYSTEM: 'Web',
    SOFTWARE_VERSION: '2025.1',
    FEATURE_LIST: [
      'Calcul du salaire brut et net',
      'Grille indiciaire officielle 2025',
      'Primes et indemnités IDE',
      'Simulation temps partiel',
      'Majorations outre-mer',
      'Calcul des impôts'
    ]
  },
  ORGANIZATION: {
    NAME: 'App\'Ines',
    URL: 'https://appines.fr'
  },
  HOW_TO: {
    NAME: 'Comment calculer votre salaire d\'infirmier IDE',
    DESCRIPTION: 'Suivez ces étapes pour obtenir une estimation précise de votre salaire d\'infirmier diplômé d\'état dans la fonction publique hospitalière.',
    TOTAL_TIME: 'PT5M',
    STEPS: [
      {
        name: 'Sélectionnez votre territoire d\'affectation',
        text: 'Choisissez entre France métropolitaine ou un territoire d\'outre-mer pour appliquer les bonnes majorations salariales.',
        url: '#territory'
      },
      {
        name: 'Choisissez votre grade',
        text: 'Sélectionnez votre grade IDE : Grade 1 (infirmier en soins généraux) ou Grade 2 (infirmier en soins généraux et spécialisés).',
        url: '#grade'
      },
      {
        name: 'Sélectionnez votre échelon',
        text: 'Choisissez votre échelon dans la grille indiciaire (de 1 à 11) selon votre ancienneté et votre progression de carrière.',
        url: '#echelon'
      },
      {
        name: 'Configurez vos primes et indemnités',
        text: 'Activez et configurez vos primes : Prime Ségur, PSS, ISS, indemnités de résidence, transport, etc.',
        url: '#primes'
      },
      {
        name: 'Consultez vos résultats',
        text: 'Obtenez votre estimation de salaire net mensuel et annuel, avec le détail des calculs et des primes.',
        url: '#results'
      }
    ]
  },
  FAQ: [
    {
      question: 'Comment est calculé le salaire d\'un infirmier IDE dans la fonction publique hospitalière ?',
      answer: 'Le salaire d\'un infirmier IDE est calculé selon la grille indiciaire officielle basée sur l\'indice majoré. Il comprend le traitement de base (indice majoré × valeur du point d\'indice), les primes et indemnités (Ségur, PSS, ISS, etc.), moins les cotisations sociales (22%) et l\'impôt sur le revenu.'
    },
    {
      question: 'Quelles sont les principales primes pour les infirmiers IDE ?',
      answer: 'Les principales primes pour les infirmiers IDE incluent : la Prime Ségur (238€/mois), la Prime Spéciale de Sujétion (PSS), l\'Indemnité de Sujétion Spéciale (ISS), l\'indemnité de résidence, l\'indemnité transport, la prime Veil, et diverses primes selon le service (soins critiques, nuit, etc.).'
    },
    {
      question: 'Quelle est la différence entre Grade 1 et Grade 2 pour les infirmiers IDE ?',
      answer: 'Le Grade 1 est le grade de recrutement pour les IDE diplômés (indices majorés de 395 à 678). Le Grade 2 est accessible après 4 ans d\'ancienneté et une formation spécialisée (indices majorés de 427 à 727), offrant une rémunération supérieure.'
    },
    {
      question: 'Comment fonctionne la progression dans les échelons ?',
      answer: 'La progression dans les échelons se fait automatiquement selon l\'ancienneté. Chaque échelon a une durée d\'avancement définie (de 1 an à 4 ans). Plus l\'échelon est élevé, plus l\'indice majoré et donc le salaire augmentent.'
    },
    {
      question: 'Les infirmiers en outre-mer ont-ils des avantages salariaux ?',
      answer: 'Oui, les infirmiers affectés en outre-mer bénéficient de majorations salariales : +25% pour la plupart des territoires, +40% pour Mayotte, plus des compléments temporaires et des abattements fiscaux pouvant aller jusqu\'à 40%.'
    },
    {
      question: 'Ce simulateur est-il officiel ?',
      answer: 'Ce simulateur utilise les grilles indiciaires officielles et les montants de primes en vigueur en 2025. Cependant, les montants affichés sont indicatifs et peuvent varier selon votre établissement et votre situation personnelle. Consultez votre service RH pour les montants exacts.'
    }
  ]
} as const;

// Helper functions to generate complete URLs
export function getCompleteImageUrl(imagePath: string): string {
  return `${SITE_CONFIG.BASE_URL}${imagePath}`;
}

export function getCanonicalUrl(path: string = ''): string {
  const resolvedPath = path || (typeof window !== 'undefined'
    ? window.location.pathname.replace(/\/+$/, '') || '/'
    : '/');
  return `${SITE_CONFIG.CANONICAL_DOMAIN}${resolvedPath}`;
}

export function getCurrentUrl(): string {
  if (typeof window !== 'undefined') {
    return window.location.href;
  }
  return SITE_CONFIG.BASE_URL;
}

// Generate meta tags for different contexts
export function generateMetaTags(
  context: 'default' | 'shared' | 'embed' = 'default',
  profession?: any,
  territory?: any,
  grade?: any,
  echelon?: any
) {
  const baseUrl = getCurrentUrl();
  
  // Generate dynamic title and description based on context
  let dynamicTitle = SEO_META.TITLE;
  let dynamicDescription = SEO_META.DESCRIPTION;
  
  if (profession) {
    // Build dynamic title with action verbs and qualitative adjectives
    let titleParts = [];
    
    // Action verb variations based on context
    const actionVerbs = [
      'Découvrez votre salaire',
      'Calculez votre rémunération',
      'Estimez vos revenus',
      'Simulez votre salaire'
    ];
    
    // Select action verb based on profession and context
    let selectedAction = actionVerbs[0]; // Default
    if (profession.id === 'infirmiere-anesthesiste-iade') {
      selectedAction = 'Découvrez votre salaire d\'IADE';
    } else if (profession.id === 'infirmiere-bloc-operatoire-ibode') {
      selectedAction = 'Calculez votre rémunération d\'IBODE';
    } else if (profession.id === 'infirmiere-puericultrice-ipde') {
      selectedAction = 'Estimez vos revenus d\'IPDE';
    } else if (profession.id === 'infirmiere-pratique-avancee-ipa') {
      selectedAction = 'Simulez votre salaire d\'IPA';
    } else {
      selectedAction = 'Découvrez votre salaire d\'IDE';
    }
    
    titleParts.push(selectedAction);
    
    if (grade && echelon) {
      // Add qualitative descriptors for grade/echelon combinations
      const gradeDescriptor = grade.id === 1 ? 'débutant' : 'expérimenté';
      const echelonDescriptor = echelon.echelon <= 3 ? 'junior' : 
                               echelon.echelon <= 7 ? 'confirmé' : 'senior';
      titleParts.push(`${gradeDescriptor} Grade ${grade.id} Échelon ${echelon.echelon} (${echelonDescriptor})`);
    }
    
    if (territory && territory.id) {
      // Add territory with qualitative context
      const territoryContext = territory.majoration > 30 ? 'avantageux' : 'attractif';
      titleParts.push(`en ${territory.name} (${territoryContext})`);
    }
    
    titleParts.push('- Simulateur Officiel 2025 | App\'Ines');
    dynamicTitle = titleParts.join(' ');
    
    // Build dynamic description with comprehensive value proposition
    let descParts = [];
    
    // Opening value proposition
    if (grade && echelon) {
      descParts.push(`Simulation précise et gratuite de votre salaire de ${profession.name.toLowerCase()} au Grade ${grade.id}, Échelon ${echelon.echelon}`);
    } else {
      descParts.push(`Calculez gratuitement et précisément votre salaire de ${profession.name.toLowerCase()}`);
    }
    
    // Territory-specific advantages
    if (territory && territory.id) {
      let territoryAdvantages = [];
      if (territory.majoration) {
        territoryAdvantages.push(`+${territory.majoration}% de majoration salariale`);
      }
      if (territory.complement) {
        territoryAdvantages.push(`+${territory.complement}% de complément temporaire`);
      }
      if (territory.abatementRate) {
        territoryAdvantages.push(`${territory.abatementRate}% d'abattement fiscal`);
      }
      
      if (territoryAdvantages.length > 0) {
        descParts.push(`en ${territory.name} avec ${territoryAdvantages.join(', ')}`);
      } else {
        descParts.push(`en ${territory.name}`);
      }
    }
    
    // Key features and benefits
    const features = [
      'grille indiciaire officielle 2025',
      'primes Ségur (CTI)',
      'PSS et ISS incluses'
    ];
    
    // Add territory-specific features
    if (territory && territory.id) {
      features.push('majorations DOM-TOM');
    }
    
    // Add profession-specific features
    if (profession.id !== 'infirmier') {
      features.push('grille spécialisée');
    }
    
    descParts.push(`Inclut ${features.join(', ')} et calcul des cotisations sociales`);
    
    // Call to action
    const ctaVariations = [
      'Simulation instantanée et confidentielle',
      'Résultats immédiats et personnalisés',
      'Calcul détaillé en temps réel',
      'Estimation fiable et actualisée'
    ];
    
    // Select CTA based on profession
    let selectedCTA = ctaVariations[0];
    if (profession.id === 'infirmiere-anesthesiste-iade') {
      selectedCTA = ctaVariations[1];
    } else if (profession.id === 'infirmiere-bloc-operatoire-ibode') {
      selectedCTA = ctaVariations[2];
    } else if (profession.id === 'infirmiere-puericultrice-ipde') {
      selectedCTA = ctaVariations[3];
    }
    
    descParts.push(selectedCTA);
    
    dynamicDescription = descParts.join('. ') + '.';
  }
  
  return {
    title: dynamicTitle,
    description: dynamicDescription,
    canonical: getCanonicalUrl(),
    openGraph: {
      type: OPEN_GRAPH_META.TYPE,
      url: baseUrl,
      title: dynamicTitle,
      description: dynamicDescription,
      image: BANNER_CONFIG.IMAGE_URL,
      imageWidth: BANNER_CONFIG.IMAGE_WIDTH,
      imageHeight: BANNER_CONFIG.IMAGE_HEIGHT,
      siteName: SITE_CONFIG.SITE_NAME,
      locale: OPEN_GRAPH_META.LOCALE
    },
    twitter: {
      card: TWITTER_META.CARD,
      url: baseUrl,
      title: dynamicTitle,
      description: dynamicDescription,
      image: BANNER_CONFIG.IMAGE_URL,
      site: TWITTER_META.SITE,
      creator: TWITTER_META.CREATOR
    },
    whatsapp: {
      title: dynamicTitle,
      description: dynamicDescription,
      image: BANNER_CONFIG.IMAGE_URL
    }
  };
}